import clsx from "clsx";
import {
  ArrowLeft,
  CircleStop,
  Clipboard,
  Clock3,
  GitBranch,
  Pause,
  Pencil,
  Play,
  Plus,
  Trash2,
  Zap,
} from "lucide-react";
import { useState } from "react";

import {
  useCancelCircuitRun,
  useCircuit,
  useCircuits,
  useDeleteCircuit,
  useTriggerCircuit,
  useUpdateCircuit,
} from "@/hooks/useCircuits";
import { useStore } from "@/lib/store";
import type {
  Circuit,
  CircuitListOwnerScope,
  CircuitRunStatus,
  CircuitTriggerConfig,
  ManagedOwnerScope,
} from "@/lib/types";
import { relativeTime } from "@/lib/utils";

import { PageViewHeader } from "../PageViewHeader";
import { SidebarToggleButton } from "../SidebarToggleButton";
import { CircuitEditor } from "./CircuitEditor";
import { CircuitGraph } from "./CircuitGraph";

function triggerLabel(trigger: CircuitTriggerConfig): string {
  if (trigger.type === "manual") return "Manual";
  if (trigger.type === "cron") return `${trigger.cron} (${trigger.timezone})`;
  if (trigger.type === "webhook") return `Webhook / ${trigger.webhookId ?? "pending"}`;
  const entity = trigger.entityIdentifier ?? trigger.entityId ?? trigger.entityType;
  return `${trigger.source} / ${entity} / ${trigger.condition.type}`;
}

function statusClass(status: CircuitRunStatus | Circuit["status"]): string {
  if (status === "enabled" || status === "completed") {
    return "border-emerald-500/30 bg-emerald-500/10 text-emerald-400";
  }
  if (status === "failed") return "border-red-400/30 bg-red-400/10 text-red-400";
  if (["pending", "running", "waiting", "cancelling"].includes(status)) {
    return "border-amber-400/30 bg-amber-400/10 text-amber-300";
  }
  return "border-cyber-border bg-cyber-muted/10 text-cyber-muted";
}

function CircuitCard({
  circuit,
  onOpen,
  showOwner,
}: {
  circuit: Circuit;
  onOpen: () => void;
  showOwner: boolean;
}): React.JSX.Element {
  const agents = circuit.definition.nodes.filter((node) => node.type === "agent").length;
  const decisions = circuit.definition.nodes.filter(
    (node) => node.type === "agent" && node.outcomes?.length,
  ).length;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group border-cyber-border bg-cyber-surface/20 hover:border-cyber-cyan/40 hover:bg-cyber-surface/35 w-full rounded-lg border p-4 text-left transition-colors"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-cyber-text truncate text-sm font-medium">{circuit.name}</h3>
            <span
              className={clsx(
                "rounded border px-1.5 py-0.5 text-[9px] uppercase",
                statusClass(circuit.status),
              )}
            >
              {circuit.status}
            </span>
          </div>
          <p className="text-cyber-muted mt-1 line-clamp-2 text-xs">
            {circuit.description || "No description"}
          </p>
          {showOwner && (
            <p className="text-cyber-muted/70 mt-1 truncate text-[10px]">
              Owned by {circuit.ownerEmail}
            </p>
          )}
        </div>
        <GitBranch
          size={18}
          className="text-cyber-muted group-hover:text-cyber-cyan shrink-0 transition-colors"
        />
      </div>
      <div className="border-cyber-border text-cyber-muted mt-4 flex flex-wrap gap-x-4 gap-y-1 border-t pt-3 text-[10px]">
        <span>{circuit.definition.nodes.length} steps</span>
        <span>
          {agents} agent{agents === 1 ? "" : "s"}
        </span>
        {decisions > 0 && (
          <span className="text-cyber-cyan">
            {decisions} decision step{decisions === 1 ? "" : "s"}
          </span>
        )}
        <span>{circuit.triggers.map((trigger) => trigger.kind).join(" + ")}</span>
        <span className="ml-auto">v{circuit.version}</span>
      </div>
    </button>
  );
}

function CircuitDetail({
  circuitId,
  onBack,
}: {
  circuitId: string;
  onBack: () => void;
}): React.JSX.Element {
  const { data, isLoading } = useCircuit(circuitId);
  const triggerCircuit = useTriggerCircuit();
  const updateCircuit = useUpdateCircuit();
  const deleteCircuit = useDeleteCircuit();
  const cancelRun = useCancelCircuitRun();
  const showToast = useStore((state) => state.showToast);
  const selectSession = useStore((state) => state.selectSession);
  const [editing, setEditing] = useState(false);
  const circuit = data?.circuit;

  async function runNow(): Promise<void> {
    try {
      await triggerCircuit.mutateAsync(circuitId);
      showToast("Circuit started", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Failed to start circuit", "error");
    }
  }

  async function toggleStatus(): Promise<void> {
    if (!circuit) return;
    try {
      await updateCircuit.mutateAsync({
        id: circuit.id,
        status: circuit.status === "enabled" ? "disabled" : "enabled",
      });
      showToast(circuit.status === "enabled" ? "Circuit paused" : "Circuit enabled", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Failed to update circuit", "error");
    }
  }

  async function deleteDisabledCircuit(): Promise<void> {
    if (
      !circuit ||
      circuit.status !== "disabled" ||
      !window.confirm(`Delete "${circuit.name}" permanently? This cannot be undone.`)
    ) {
      return;
    }
    try {
      await deleteCircuit.mutateAsync(circuit.id);
      showToast("Circuit deleted", "success");
      onBack();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Failed to delete circuit", "error");
    }
  }

  async function copyValue(value: string, label: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      showToast(`${label} copied`, "success");
    } catch {
      showToast(`Could not copy ${label.toLowerCase()}`, "error");
    }
  }

  if (isLoading || !circuit) {
    return <div className="text-cyber-muted p-6 text-sm">Loading circuit...</div>;
  }

  if (editing) {
    return (
      <CircuitEditor
        circuit={circuit}
        ownerScope={
          ["system@sherlog.local", "cli@sherlog.local"].includes(circuit.ownerEmail)
            ? "system"
            : "personal"
        }
        onClose={() => setEditing(false)}
        onSaved={() => setEditing(false)}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-cyber-border flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2.5 md:px-5">
        <div className="flex min-w-0 items-center gap-2">
          <SidebarToggleButton />
          <button
            type="button"
            onClick={onBack}
            className="text-cyber-muted hover:text-cyber-cyan p-1"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="min-w-0">
            <h2 className="text-cyber-text truncate text-sm font-medium">{circuit.name}</h2>
            <p className="text-cyber-muted text-[10px]">
              Version {circuit.version} / {circuit.ownerEmail}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="border-cyber-border text-cyber-muted hover:border-cyber-cyan/40 hover:text-cyber-cyan flex items-center gap-1 rounded border px-2.5 py-1.5 text-xs"
          >
            <Pencil size={13} /> Edit
          </button>
          <button
            type="button"
            onClick={toggleStatus}
            className="border-cyber-border text-cyber-muted hover:border-cyber-cyan/40 hover:text-cyber-cyan flex items-center gap-1 rounded border px-2.5 py-1.5 text-xs"
          >
            {circuit.status === "enabled" ? <Pause size={13} /> : <Play size={13} />}
            {circuit.status === "enabled" ? "Pause" : "Enable"}
          </button>
          {circuit.status === "disabled" && (
            <button
              type="button"
              onClick={deleteDisabledCircuit}
              disabled={deleteCircuit.isPending}
              className="flex items-center gap-1 rounded border border-red-400/30 px-2.5 py-1.5 text-xs text-red-400 hover:bg-red-400/10 disabled:opacity-40"
            >
              <Trash2 size={13} /> Delete
            </button>
          )}
          <button
            type="button"
            onClick={runNow}
            disabled={circuit.status !== "enabled" || triggerCircuit.isPending}
            className="border-cyber-cyan/40 bg-cyber-cyan/15 text-cyber-cyan hover:bg-cyber-cyan/25 flex items-center gap-1 rounded border px-2.5 py-1.5 text-xs disabled:opacity-40"
          >
            <Zap size={13} /> Run now
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto grid max-w-7xl gap-6 p-4 md:p-6 xl:grid-cols-[minmax(0,1fr)_380px]">
          <div className="space-y-6">
            <section>
              <div className="mb-3 flex items-center gap-2">
                <GitBranch size={15} className="text-cyber-cyan" />
                <h3 className="text-cyber-text text-sm font-medium">Execution flow</h3>
              </div>
              <CircuitGraph definition={circuit.definition} />
            </section>

            <section>
              <div className="mb-3 flex items-center gap-2">
                <Clock3 size={15} className="text-cyber-cyan" />
                <h3 className="text-cyber-text text-sm font-medium">Recent runs</h3>
              </div>
              <div className="border-cyber-border divide-cyber-border divide-y overflow-hidden rounded border">
                {(data?.runs ?? []).map((run) => {
                  const active = ["pending", "running", "waiting", "cancelling"].includes(
                    run.status,
                  );
                  const steps = data?.steps.filter((step) => step.runId === run.id) ?? [];
                  return (
                    <div key={run.id} className="bg-cyber-surface/20 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={clsx(
                              "rounded border px-1.5 py-0.5 text-[9px] uppercase",
                              statusClass(run.status),
                            )}
                          >
                            {run.status}
                          </span>
                          <span className="text-cyber-muted font-mono text-[10px]">{run.id}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {run.status !== "skipped" && (
                            <button
                              type="button"
                              onClick={() => selectSession(run.threadKey)}
                              className="text-cyber-muted hover:text-cyber-cyan text-[10px]"
                            >
                              Open session
                            </button>
                          )}
                          {active && run.status !== "cancelling" && (
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  await cancelRun.mutateAsync({ id: circuit.id, runId: run.id });
                                  showToast("Cancellation requested", "success");
                                } catch (error) {
                                  showToast(
                                    error instanceof Error ? error.message : "Failed to cancel",
                                    "error",
                                  );
                                }
                              }}
                              className="text-cyber-muted hover:text-red-400"
                              aria-label="Cancel run"
                            >
                              <CircleStop size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="text-cyber-muted mt-2 flex flex-wrap gap-x-3 text-[10px]">
                        <span>{relativeTime(run.startedAt)}</span>
                        {run.currentNodeId && <span>Current: {run.currentNodeId}</span>}
                      </div>
                      {run.errorMessage && (
                        <p className="mt-2 text-xs text-red-300">{run.errorMessage}</p>
                      )}
                      {run.status === "skipped" &&
                        typeof run.result?.skippedReason === "string" && (
                          <p className="text-cyber-muted mt-2 text-xs">
                            {run.result.skippedReason}
                          </p>
                        )}
                      {steps.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {steps.map((step) => (
                            <span
                              key={step.jobId}
                              title={step.response ?? step.errorMessage ?? undefined}
                              className="border-cyber-border bg-cyber-bg/60 text-cyber-muted rounded border px-2 py-1 text-[10px]"
                            >
                              {step.nodeId}
                              {step.outcome && (
                                <span className="text-cyber-cyan"> -&gt; {step.outcome}</span>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {(data?.runs.length ?? 0) === 0 && (
                  <div className="text-cyber-muted bg-cyber-surface/20 p-5 text-center text-xs">
                    No runs yet
                  </div>
                )}
              </div>
            </section>
          </div>

          <aside className="space-y-4">
            <section className="border-cyber-border bg-cyber-surface/25 rounded-lg border p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={clsx(
                    "rounded border px-1.5 py-0.5 text-[9px] uppercase",
                    statusClass(circuit.status),
                  )}
                >
                  {circuit.status}
                </span>
                <span className="text-cyber-muted text-[10px]">Owned by {circuit.ownerEmail}</span>
              </div>
              <p className="text-cyber-muted mt-3 text-xs">
                {circuit.description || "No description"}
              </p>
            </section>
            <section className="border-cyber-border bg-cyber-surface/25 rounded-lg border p-4">
              <h3 className="text-cyber-text mb-3 text-xs font-medium tracking-wider uppercase">
                Triggers
              </h3>
              {circuit.triggers.some((trigger) => trigger.kind === "cron") && (
                <p className="text-cyber-muted mb-3 text-xs">
                  Scheduled overlap:{" "}
                  {circuit.cronOverlapPolicy === "allow"
                    ? "Allow independent runs"
                    : "Skip while a scheduled run is active"}
                </p>
              )}
              <div className="space-y-2">
                {circuit.triggers.map((trigger) => (
                  <div
                    key={trigger.id}
                    className="border-cyber-border bg-cyber-bg/50 rounded border p-2.5"
                  >
                    <div className="text-cyber-cyan text-[10px] tracking-wider uppercase">
                      {trigger.kind}
                    </div>
                    <div className="text-cyber-muted mt-1 text-xs break-words">
                      {triggerLabel(trigger.config)}
                    </div>
                    {trigger.kind === "webhook" && trigger.webhookUrl && trigger.webhookSecret && (
                      <div className="mt-2 space-y-1.5">
                        {[
                          ["Webhook URL", trigger.webhookUrl],
                          ["Bearer secret", trigger.webhookSecret],
                        ].map(([label, value]) => (
                          <div key={label}>
                            <div className="text-cyber-muted/70 mb-0.5 text-[10px]">{label}</div>
                            <div className="border-cyber-border bg-cyber-bg flex min-w-0 items-center gap-1 rounded border px-2 py-1.5">
                              <code className="text-cyber-text min-w-0 flex-1 truncate text-[10px]">
                                {value}
                              </code>
                              <button
                                type="button"
                                onClick={() => void copyValue(value, label)}
                                className="text-cyber-muted hover:text-cyber-cyan shrink-0 p-0.5"
                                aria-label={`Copy ${label.toLowerCase()}`}
                              >
                                <Clipboard size={12} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {trigger.nextRunAt && (
                      <div className="text-cyber-muted/70 mt-1 text-[10px]">
                        Next {relativeTime(trigger.nextRunAt)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}

export function CircuitsView(): React.JSX.Element {
  const [ownerScope, setOwnerScope] = useState<CircuitListOwnerScope>("personal");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creatingScope, setCreatingScope] = useState<ManagedOwnerScope | null>(null);
  const isAdmin = useStore((state) => state.user?.role === "admin");
  const { data, isLoading } = useCircuits(ownerScope);

  if (creatingScope) {
    return (
      <CircuitEditor
        ownerScope={creatingScope}
        onClose={() => setCreatingScope(null)}
        onSaved={(circuit) => {
          setCreatingScope(null);
          setSelectedId(circuit.id);
        }}
      />
    );
  }

  if (selectedId)
    return <CircuitDetail circuitId={selectedId} onBack={() => setSelectedId(null)} />;

  const circuits = data?.circuits ?? [];
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageViewHeader
        title="Circuits"
        badge={
          circuits.length ? (
            <span className="bg-cyber-surface text-cyber-muted rounded-full px-1.5 py-0.5 text-[10px]">
              {circuits.filter((circuit) => circuit.status === "enabled").length} active
            </span>
          ) : undefined
        }
        actions={
          <>
            {isAdmin && (
              <div className="border-cyber-border flex overflow-hidden rounded border">
                {(["personal", "system", "all"] as const).map((scope) => (
                  <button
                    key={scope}
                    type="button"
                    onClick={() => setOwnerScope(scope)}
                    className={clsx(
                      "px-2 py-1 text-xs capitalize",
                      ownerScope === scope
                        ? "bg-cyber-cyan/15 text-cyber-cyan"
                        : "text-cyber-muted hover:text-cyber-text",
                    )}
                  >
                    {scope}
                  </button>
                ))}
              </div>
            )}
            {ownerScope !== "all" && (
              <button
                type="button"
                onClick={() => setCreatingScope(ownerScope)}
                className="border-cyber-cyan/40 bg-cyber-cyan/10 text-cyber-cyan hover:bg-cyber-cyan/20 flex items-center gap-1 rounded border px-2.5 py-1.5 text-xs"
              >
                <Plus size={13} /> New circuit
              </button>
            )}
          </>
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6">
        <div className="mx-auto max-w-6xl">
          <div className="mb-5">
            <div className="text-cyber-cyan text-[10px] tracking-[0.2em] uppercase">
              Durable execution
            </div>
            <h2 className="text-cyber-text mt-1 text-xl font-semibold">
              Make the next step explicit
            </h2>
            <p className="text-cyber-muted mt-1 max-w-2xl text-xs leading-relaxed">
              Chain agent work, durable waits, and external signals. Decision steps declare optional
              outcomes so Sherlog can record the choice and route safely.
            </p>
          </div>
          {isLoading ? (
            <div className="text-cyber-muted py-12 text-center text-sm">Loading circuits...</div>
          ) : circuits.length === 0 ? (
            <div className="border-cyber-border bg-cyber-surface/20 rounded-lg border border-dashed p-10 text-center">
              <GitBranch size={28} className="text-cyber-cyan/70 mx-auto" />
              <h3 className="text-cyber-text mt-3 text-sm font-medium">No circuits yet</h3>
              <p className="text-cyber-muted mt-1 text-xs">
                Start with an agent step, then add a wait or decision route.
              </p>
              {ownerScope !== "all" && (
                <button
                  type="button"
                  onClick={() => setCreatingScope(ownerScope)}
                  className="text-cyber-cyan mt-4 text-xs hover:underline"
                >
                  Create your first circuit
                </button>
              )}
            </div>
          ) : (
            <div className="grid gap-3 lg:grid-cols-2">
              {circuits.map((circuit) => (
                <CircuitCard
                  key={circuit.id}
                  circuit={circuit}
                  onOpen={() => setSelectedId(circuit.id)}
                  showOwner={ownerScope === "all"}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
